import React, { useEffect, useState } from 'react'
// MUI
import { Container, Grid, Skeleton } from "@mui/material"
// interfaces
import { QrcodeFields } from "./interfaces/interfaces"
// API
import { APIGet, APIGetBlob, HttpResponse } from "./api/apiCalls"
// misc
import { isMobile } from 'react-device-detect'
// QR Codes types components
import * as QRCode from './qrcodes'
import {styles} from "./mainStyle";

const { REACT_APP_RESOLVER_URL } = process.env;

// redirectToResolver404 will redirect the user to our regular 404 page
const redirectToResolver404 = () => {
    // console.log("Redirecting to 404");
    // This URL will generate a 404 on the resolver
    window.location.href = "https://qrcode.link/a/";
}

export default function App() {

    // it should **always** be present in our code
    const [loading, setLoading] = useState(true);
    const [dynamicType, setDynamicType] = useState<string>('');
    const [fieldDisplay, setFieldDisplay] = useState<QrcodeFields>({
        type: null,
        text: '',
        text_rendering_raw: false,
        phone: '',
        message: '',
        to: '',
        subject: '',
        body: '',
        lat: '',
        lon: '',
        end_date: '',
        location: '',
        start_date: '',
        title: '',
        password: '',
        security: '',
        ssid: '',
        time_zone: '',
    });
    const [isParsedMobile, setIsParsedMobile] = useState<boolean>(false);

    useEffect(() => {
        if (navigator.userAgentData !== undefined) {
            setIsParsedMobile(navigator.userAgentData.mobile);
        } else {
            setIsParsedMobile(isMobile);
        }
    }, [])

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const domain = urlParams.get("domain");
        let resAddr = `${REACT_APP_RESOLVER_URL}`;
        if (typeof (domain) === "string" && domain !== "") {
            resAddr = "https://" + domain;
        }

        // window.history.replaceState({}, document.title, window.location.href.split("?")[0]);

        const fragments = window.location.pathname.split("/");
        // check if request is for type text, sms, email etc.
        const type = fragments[fragments.length - 2];
        // id of the qrcode short_url (https://short_base_url/type/id)
        const id = fragments[fragments.length - 1].split('?')[0];

        //https://sandbox.qrcode.link/t/aVpkXg
        //http://localhost:3001/t/aVpkXg
        APIGet<QrcodeFields>(`${resAddr}/${type}/${id}`).then((data) => {
            // If the data is well parsed then set state to response payload
            if (data.ok && data.parsedBody !== undefined) {
                setDynamicType(data.parsedBody.type!);
                setFieldDisplay(data.parsedBody);
            } else {
                // If an error has occured or payload is empty / corrupted then redirect to resolver 404
                redirectToResolver404();
            }
        }).catch(() => {
            // If an error has occured or payload is empty / corrupted then redirect to resolver 404
            redirectToResolver404();
        }).finally(() => {
            // In both cases the app has finished loading, we can update the state accordingly
            setLoading(false);
        });
    }, [])

    const DownloadBlob = (fileName: string, blob: Blob) => {
        const anchor = window.document.createElement('a');
        anchor.href = window.URL.createObjectURL(blob);
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(anchor.href);
    }

    const DownloadFile = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const domain = urlParams.get("domain");
        let resAddr = `${REACT_APP_RESOLVER_URL}`;
        if (typeof (domain) === "string" && domain !== "") {
            resAddr = "https://" + domain;
        }
        const fragments = window.location.pathname.split("/");
        // check if request is for type text, sms, email etc.
        const type = fragments[fragments.length - 2];
        // id of the qrcode short_url (https://short_base_url/type/id)
        const id = fragments[fragments.length - 1].split('?')[0];


        APIGetBlob<any>(`${resAddr}/${type}/${id}/download`)
            .then((data: HttpResponse<any>) => {
                if (data.ok && data.rawBlob) {
                    DownloadBlob(data.headers.get("filename") || `${id}`, data.rawBlob)
                }
            })
            .catch((err: any) => {
                console.log("Err => ", err);
            })
    }

    const copyContentToClipBoard = (content: string) => {
        if (content === "") {
            return
        }

        navigator.clipboard.writeText(content)
            .then(() => {
                console.log("copied");
            })
            .catch(err => {
                console.log("err", err)
            });
    }

    const typeDisplay = () => {

        /*
        *  TYPES:
        *   t:      text
        *   w:      wifi
        *   ev:     calendar (event)
        *   sms:    sms
        *   g:      geoloc
        *   e:      email
        *   p:      call
        * */
        switch (dynamicType) {
            case 't':
                return <QRCode.Text fieldDisplay={fieldDisplay} copyContentToClipBoard={copyContentToClipBoard} />;
            case 'w':
                return <QRCode.Wifi fieldDisplay={fieldDisplay} copyContentToClipBoard={copyContentToClipBoard} />;
            case 'ev':
                return <QRCode.Event fieldDisplay={fieldDisplay} DownloadIcsFile={DownloadFile} />;
            case 'sms':
                return <QRCode.Sms fieldDisplay={fieldDisplay} />;
            case 'g':
                return <QRCode.Location fieldDisplay={fieldDisplay} isParsedMobile={isParsedMobile} copyContentToClipBoard={copyContentToClipBoard} />;
            case 'e':
                return <QRCode.Email fieldDisplay={fieldDisplay} />;
            case 'p':
                return <QRCode.Phone fieldDisplay={fieldDisplay} />;
            case 'm':
                return <QRCode.Media fieldDisplay={fieldDisplay} />;
                    default:
                return <Grid item xs={12}></Grid>;
        }
    }

    return (
        <Container maxWidth={"md"}>
            <Grid item xs={12}>
                <Grid container justifyContent={"center"} sx={styles.mainGridWrapper}>
                    {
                        loading ?
                            <Skeleton sx={styles.skeleton} variant="rectangular" width={300} height={118} />
                            : typeDisplay()
                    }
                    <Grid item xs={12} style={styles.firstGridItem}></Grid>
                    <Grid item xs={4} sm={9}></Grid>
                    <Grid item xs={8} sm={3} sx={styles.footerWrapper}>
                        {/* Incomatible styles typescript-only bug - seems like TS doesn't like absolute positioning */}
                        {/* @ts-ignore */}
                        Powered By <img style={styles.footerContent} alt={'logo unitag.io'} src={'/unitag-logo-hd.png'} />
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    );
}
